import { useCallback, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { ImageUp, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PixelCrop } from '@/lib/crop-image';
import { cn } from '@/lib/utils';

// Opciones de proporción para el recorte libre (imágenes de cuerpo).
const ASPECT_OPTIONS: { label: string; value: number | 'original' }[] = [
  { label: 'Original', value: 'original' },
  { label: '16:9', value: 16 / 9 },
  { label: '3:2', value: 3 / 2 },
  { label: '4:3', value: 4 / 3 },
  { label: '1:1', value: 1 },
];

interface BlogImageFieldProps {
  title: string;
  hint: string;
  /** Aspect ratio bloqueado (destacada/thumbnail). Si se omite, el
   *  usuario elige la proporción (imágenes de cuerpo). */
  aspect?: number;
  /** URL ya subida (modo edición), si existe. */
  existingUrl?: string;
  /** Sube el original + recorte y devuelve la URL final. */
  onUpload(file: File, crop: PixelCrop): Promise<string>;
}

/**
 * Gestor de una imagen del blog: drag&drop / selección, recorte
 * (react-easy-crop) y subida inmediata (la entrada ya tiene id). Para
 * destacada/thumbnail el ratio está bloqueado; para el cuerpo el usuario
 * elige la proporción o deja la original.
 */
export function BlogImageField({
  title,
  hint,
  aspect,
  existingUrl,
  onUpload,
}: BlogImageFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [editorSrc, setEditorSrc] = useState<string | null>(null);
  const [editorFile, setEditorFile] = useState<File | null>(null);
  const [naturalAspect, setNaturalAspect] = useState(3 / 2);
  const [editorAspect, setEditorAspect] = useState<number>(aspect ?? 3 / 2);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);

  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);

  const openEditor = useCallback(
    (file: File) => {
      const src = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const ratio = img.naturalWidth / img.naturalHeight;
        setNaturalAspect(ratio);
        setEditorAspect(aspect ?? ratio);
      };
      img.src = src;
      setEditorFile(file);
      setEditorSrc(src);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAreaPixels(null);
    },
    [aspect],
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (file && file.type.startsWith('image/')) openEditor(file);
    },
    [openEditor],
  );

  const confirmCrop = useCallback(async () => {
    if (!editorFile || !editorSrc || !areaPixels) return;
    const cropPx: PixelCrop = {
      x: areaPixels.x,
      y: areaPixels.y,
      width: areaPixels.width,
      height: areaPixels.height,
    };
    setUploading(true);
    try {
      const url = await onUpload(editorFile, cropPx);
      setUploadedUrl(url);
      URL.revokeObjectURL(editorSrc);
      setEditorSrc(null);
      setEditorFile(null);
    } finally {
      setUploading(false);
    }
  }, [editorFile, editorSrc, areaPixels, onUpload]);

  const cancelEditor = useCallback(() => {
    if (editorSrc) URL.revokeObjectURL(editorSrc);
    setEditorSrc(null);
    setEditorFile(null);
  }, [editorSrc]);

  const shownUrl = uploadedUrl ?? existingUrl ?? null;
  const previewAspect = aspect ?? 16 / 9;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">{title}</h4>
        {shownUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            Cambiar
          </Button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {shownUrl ? (
        <div
          className="relative overflow-hidden rounded-lg border bg-muted"
          style={{ aspectRatio: previewAspect }}
        >
          <img src={shownUrl} alt="" className="size-full object-cover" />
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Cambiar imagen"
            onClick={() => inputRef.current?.click()}
            className="absolute right-2 top-2 size-7"
          >
            <X className="size-4" aria-hidden />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground transition-colors',
            dragOver ? 'border-foreground bg-muted' : 'hover:bg-muted/50',
          )}
          style={{ aspectRatio: previewAspect }}
        >
          <ImageUp className="size-6" aria-hidden />
          <span>Arrastra una imagen o haz clic para subir</span>
          <span className="text-xs text-muted-foreground/70">{hint}</span>
        </button>
      )}

      <Dialog open={Boolean(editorSrc)} onOpenChange={(o) => !o && !uploading && cancelEditor()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Recortar imagen — {title}</DialogTitle>
          </DialogHeader>
          <div className="relative h-[360px] w-full overflow-hidden rounded-md bg-black">
            {editorSrc && (
              <Cropper
                image={editorSrc}
                crop={crop}
                zoom={zoom}
                aspect={editorAspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPx) => setAreaPixels(areaPx)}
              />
            )}
          </div>

          {/* Selector de proporción solo cuando el ratio no está bloqueado. */}
          {aspect === undefined && (
            <div className="flex flex-wrap gap-1">
              {ASPECT_OPTIONS.map((opt) => {
                const value = opt.value === 'original' ? naturalAspect : opt.value;
                const active = Math.abs(value - editorAspect) < 0.001;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setEditorAspect(value)}
                    className={cn(
                      'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                      active ? 'border-foreground bg-foreground text-background' : 'hover:bg-muted',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
              aria-label="Zoom"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={cancelEditor} disabled={uploading}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmCrop} disabled={!areaPixels || uploading}>
              {uploading ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden /> Subiendo…
                </>
              ) : (
                'Aplicar y subir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
